import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ProviderBottomNav from '../../components/common/ProviderBottomNav';
import { useBookings, api } from '../../hooks/useApi';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import UnifiedMap from '../../components/common/UnifiedMap';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ArrowLeft, Clock, CheckCircle, MapPin, ChevronRight, ChevronLeft, Phone, Navigation,
  Play, Square, Locate, Briefcase, ExternalLink, Users, UserCheck, Loader2,
  AlertTriangle, Flag, XCircle, Shield, Info
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const REPORT_REASONS = [
  { id: 'scam', label: 'Scam / Fraudulent Booking' },
  { id: 'no_show', label: 'Customer No-Show' },
  { id: 'harassment', label: 'Harassment / Abusive Behavior' },
  { id: 'fake_booking', label: 'Fake / Test Booking' },
  { id: 'other', label: 'Other Violation' },
];

const statusConfig = {
  accepted: { label: 'Accepted', color: 'bg-blue-100 text-blue-800', nextStatus: 'on_the_way', nextLabel: 'Start Travel' },
  on_the_way: { label: 'On the Way', color: 'bg-purple-100 text-purple-800', nextStatus: 'arrived', nextLabel: "I've Arrived" },
  arrived: { label: 'Arrived', color: 'bg-indigo-100 text-indigo-800', nextStatus: 'in_progress', nextLabel: 'Start Work' },
  in_progress: { label: 'In Progress', color: 'bg-orange-100 text-orange-800', nextStatus: 'pending_completion', nextLabel: 'Complete Job' },
  pending_completion: { label: 'Awaiting Confirmation', color: 'bg-amber-100 text-amber-800' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800' },
};

export default function ProviderJobs() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bookings, isLoading, refetch } = useBookings();
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [trackingBookingId, setTrackingBookingId] = useState(null);
  const [providerPos, setProviderPos] = useState(null);
  const watchIdRef = useRef(null);
  
  // Staff assignment states
  const [staff, setStaff] = useState([]);
  const [isCompanyProvider, setIsCompanyProvider] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(null);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  
  // Report customer states
  const [showReportDialog, setShowReportDialog] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [isReporting, setIsReporting] = useState(false);
  
  // Cancellation states
  const [showCancelDialog, setShowCancelDialog] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  // Pagination states
  const [activePage, setActivePage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  const activeBookings = bookings.filter(b => ['accepted', 'on_the_way', 'arrived', 'in_progress', 'pending_completion'].includes(b.status));
  const completedBookings = bookings.filter(b => ['completed', 'cancelled'].includes(b.status));

  // Paginated bookings
  const paginatedActiveBookings = activeBookings.slice((activePage - 1) * ITEMS_PER_PAGE, activePage * ITEMS_PER_PAGE);
  const paginatedCompletedBookings = completedBookings.slice((historyPage - 1) * ITEMS_PER_PAGE, historyPage * ITEMS_PER_PAGE);
  const totalActivePages = Math.ceil(activeBookings.length / ITEMS_PER_PAGE);
  const totalHistoryPages = Math.ceil(completedBookings.length / ITEMS_PER_PAGE);
  
  // Provider profile state for location check
  const [profile, setProfile] = useState(null);

  // Fetch provider profile and staff
  useEffect(() => {
    const fetchProviderData = async () => {
      const token = localStorage.getItem('token');
      try {
        // Check if company provider
        const profileRes = await axios.get(`${API}/provider/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProfile(profileRes.data);
        const isCompany = profileRes.data?.provider_type === 'company';
        setIsCompanyProvider(isCompany);
        
        // Fetch staff if company provider
        if (isCompany) {
          const staffRes = await axios.get(`${API}/provider/staff`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setStaff(staffRes.data || []);
        }
      } catch (e) {
        console.error('Failed to fetch provider data:', e);
      }
    };
    fetchProviderData();
  }, []);

  // Assign staff to booking
  const handleAssignStaff = async () => {
    if (!selectedStaffId) {
      toast.error('Please select a staff member');
      return;
    }
    
    setIsAssigning(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/provider/bookings/${showAssignDialog}/assign-staff`, {
        staff_id: selectedStaffId,
        notes: assignmentNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Staff assigned successfully');
      setShowAssignDialog(null);
      setSelectedStaffId('');
      setAssignmentNotes('');
      refetch();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Handle Pydantic validation error array
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg).join(', ') || 'Validation error');
      } else {
        toast.error(detail || 'Failed to assign staff');
      }
    } finally {
      setIsAssigning(false);
    }
  };

  // Report customer
  const handleReportCustomer = async () => {
    if (!reportReason) {
      toast.error('Please select a reason');
      return;
    }
    if (!reportDescription.trim()) {
      toast.error('Please provide a description');
      return;
    }
    
    setIsReporting(true);
    const token = localStorage.getItem('token');
    const booking = bookings.find(b => b.id === showReportDialog);
    
    try {
      await axios.post(`${API}/reports/user`, {
        reported_user_id: booking?.customer_id,
        booking_id: showReportDialog,
        reason: reportReason,
        description: reportDescription
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report submitted. Admin will review and take action.');
      setShowReportDialog(null);
      setReportReason('');
      setReportDescription('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg).join(', ') || 'Validation error');
      } else {
        toast.error(detail || 'Failed to submit report');
      }
    } finally {
      setIsReporting(false);
    }
  };

  // Real-time location tracking
  const startTracking = useCallback((bookingId) => {
    if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    setTrackingBookingId(bookingId);
    const token = localStorage.getItem('token');
    
    watchIdRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setProviderPos({ lat: latitude, lng: longitude });
        // Update location on server
        try {
          await axios.put(
            `${API}/bookings/${bookingId}/provider-location?latitude=${latitude}&longitude=${longitude}`,
            {}, { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (e) { console.error('Location update failed:', e); }
      },
      (err) => { console.error('Geolocation error:', err); toast.error('Unable to track location'); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
    toast.success('Live location tracking started');
  }, []);

  const stopTracking = useCallback(() => {
    if (watchIdRef.current) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; }
    setTrackingBookingId(null);
    setProviderPos(null);
  }, []);

  useEffect(() => { return () => { if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current); }; }, []);

  const handleUpdateStatus = async (bookingId, newStatus) => {
    setUpdatingId(bookingId);
    try {
      const payload = { status: newStatus };
      // If starting travel, include current location
      if (newStatus === 'on_the_way' && providerPos) {
        payload.latitude = providerPos.lat;
        payload.longitude = providerPos.lng;
      }
      await api.updateBookingStatus(bookingId, payload);
      
      // Auto-start tracking when going on_the_way
      if (newStatus === 'on_the_way') {
        startTracking(bookingId);
      }
      // Stop tracking when arrived or completed
      if (['arrived', 'completed', 'pending_completion'].includes(newStatus)) {
        stopTracking();
      }
      
      toast.success(`Status updated to ${newStatus.replace(/_/g, ' ')}`);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally { setUpdatingId(null); }
  };

  const JobCard = ({ booking, showActions = false }) => {
    const status = statusConfig[booking.status] || statusConfig.accepted;
    const isExpanded = expandedBooking === booking.id;
    const isTracking = trackingBookingId === booking.id;
    const customerName = booking.customer_name || booking.address?.name || 'Customer';
    const destLat = booking.address?.latitude;
    const destLng = booking.address?.longitude;
    const showMap = isExpanded && destLat && destLng;

    const markers = [];
    if (destLat && destLng) {
      markers.push({ lat: destLat, lng: destLng, color: 'red', label: `Service Location: ${booking.address?.full_address || ''}` });
    }
    if (isTracking && providerPos) {
      markers.push({ lat: providerPos.lat, lng: providerPos.lng, color: 'darkblue', label: 'Your Location' });
    }

    return (
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden border-l-4 border-l-amber-500 mb-5" data-testid={`job-card-${booking.id}`}>
        <div className="p-4" onClick={() => setExpandedBooking(isExpanded ? null : booking.id)} style={{ cursor: 'pointer' }}>
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="font-semibold text-slate-900">#{booking.id.slice(0, 8).toUpperCase()}</p>
              <p className="text-sm text-slate-500">{booking.scheduled_date} at {booking.scheduled_time}</p>
            </div>
            <span className={`status-badge ${status.color}`}>{status.label}</span>
          </div>
          
          {/* Customer Info */}
          <div className="flex items-center gap-2 mb-2 bg-stone-50 rounded-lg p-2">
            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-amber-700">{customerName.charAt(0)}</span>
            </div>
            <div className="min-w-0">
              <p className="font-medium text-slate-900 text-sm" data-testid={`customer-name-${booking.id}`}>{customerName}</p>
              <p className="text-xs text-slate-500 truncate">{booking.address?.full_address}</p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-stone-100">
            <span className="text-lg font-bold text-green-600">+₱{booking.provider_earnings}</span>
            <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          </div>
          
          {/* Warranty Badge */}
          {booking.has_warranty && booking.warranty_days > 0 && (
            <div className="flex items-center gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <Shield className="w-4 h-4 text-amber-600" />
              <span className="text-xs text-amber-800 font-medium">{booking.warranty_days}-day warranty</span>
            </div>
          )}
        </div>

        {/* Expanded: Map + Actions */}
        {isExpanded && (
          <div className="border-t border-stone-100 p-4 bg-stone-50 space-y-4">
            {/* Map */}
            {showMap && (
              <div data-testid={`booking-map-${booking.id}`} className="space-y-3">
                <UnifiedMap
                  center={providerPos ? [providerPos.lat, providerPos.lng] : [destLat, destLng]}
                  zoom={14}
                  height="200px"
                  markers={markers}
                />
                {/* Get Direction + tracking indicators */}
                <div className="flex items-center justify-between gap-2">
                  {isTracking && (
                    <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
                      <Locate className="w-4 h-4 text-green-600 animate-pulse" />
                      <span className="text-xs text-green-700 font-medium">Live tracking</span>
                      <Button size="sm" variant="outline" className="ml-auto text-xs h-7" onClick={(e) => { e.stopPropagation(); stopTracking(); }}>
                        <Square className="w-3 h-3 mr-1" /> Stop
                      </Button>
                    </div>
                  )}
                  {destLat && destLng && (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${destLat},${destLng}${providerPos ? `&origin=${providerPos.lat},${providerPos.lng}` : ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded-full transition-colors"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`get-direction-${booking.id}`}
                    >
                      <Navigation className="w-3.5 h-3.5" /> Navigate to Service
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {showActions && (
              <div className={`${showMap ? 'mt-5' : ''}`}>
                {/* Staff Assignment for Company Providers */}
                {isCompanyProvider && booking.status === 'accepted' && (
                  booking.assigned_staff_name ? (
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl mb-4">
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-blue-800">
                          Assigned to: <strong>{booking.assigned_staff_name}</strong>
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); setShowAssignDialog(booking.id); }}
                        className="text-xs"
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={(e) => { e.stopPropagation(); setShowAssignDialog(booking.id); }}
                      className="w-full h-11 mb-4 bg-blue-600 hover:bg-blue-700"
                      data-testid={`assign-staff-${booking.id}`}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Assign Staff
                    </Button>
                  )
                )}
                
                {status.nextStatus && (
                  <Button
                    className="w-full h-11 mb-4 bg-slate-900 hover:bg-slate-800"
                    onClick={(e) => { e.stopPropagation(); handleUpdateStatus(booking.id, status.nextStatus); }}
                    disabled={updatingId === booking.id}
                    data-testid={`update-status-${booking.id}`}
                  >
                    {status.nextStatus === 'on_the_way' && <Play className="w-4 h-4 mr-2" />}
                    {status.nextStatus === 'arrived' && <MapPin className="w-4 h-4 mr-2" />}
                    {status.nextStatus === 'completed' && <CheckCircle className="w-4 h-4 mr-2" />}
                    {updatingId === booking.id ? 'Updating...' : status.nextLabel}
                  </Button>
                )}

                {/* Start tracking manually for on_the_way status */}
                {booking.status === 'on_the_way' && !isTracking && (
                  <Button variant="outline" className="w-full h-11 mb-4" onClick={(e) => { e.stopPropagation(); startTracking(booking.id); }} data-testid={`start-tracking-${booking.id}`}>
                    <Navigation className="w-4 h-4 mr-2" /> Resume Location Tracking
                  </Button>
                )}

                <div className="flex gap-2 mb-4">
                  <Button variant="outline" size="sm" className="flex-1 h-10" onClick={(e) => { e.stopPropagation(); navigate(`/booking/${booking.id}`); }} data-testid={`view-details-${booking.id}`}>
                    View Details
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 h-10" 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      if (booking.customer_phone) {
                        window.location.href = `tel:${booking.customer_phone}`;
                      } else {
                        toast.error('Customer phone number not available');
                      }
                    }}
                    data-testid={`call-customer-${booking.id}`}
                  >
                    <Phone className="w-4 h-4 mr-1" /> Call
                  </Button>
                </div>
                
                {/* Report Customer Button */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={(e) => { e.stopPropagation(); setShowReportDialog(booking.id); }}
                  data-testid={`report-customer-${booking.id}`}
                >
                  <Flag className="w-4 h-4 mr-2" />
                  Report Customer
                </Button>
              </div>
            )}

            {/* For completed/cancelled bookings - show View Details and Report */}
            {!showActions && (
              <div className="space-y-3">
                <Button variant="outline" className="w-full" onClick={(e) => { e.stopPropagation(); navigate(`/booking/${booking.id}`); }}>
                  View Details <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={(e) => { e.stopPropagation(); setShowReportDialog(booking.id); }}
                  data-testid={`report-customer-completed-${booking.id}`}
                >
                  <Flag className="w-4 h-4 mr-2" />
                  Report Customer
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="page-container">
      <header className="bg-white border-b border-stone-200 px-4 py-4 sticky top-0 z-40">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/provider')} className="w-10 h-10 rounded-full hover:bg-stone-100 flex items-center justify-center" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h1 className="text-xl font-bold text-slate-900 font-[Poppins]">My Jobs</h1>
        </div>
      </header>

      <main className="px-4 py-6">
        {/* Location Warning - Show if provider hasn't set service location */}
        {profile && (!profile.service_latitude || !profile.service_longitude) && (
          <div 
            className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 mb-4 cursor-pointer hover:bg-amber-100 transition-colors"
            onClick={() => navigate('/provider/profile')}
            data-testid="location-warning-jobs"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse">
                <MapPin className="w-4 h-4 text-amber-700" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-amber-900 text-sm">Set Your Service Location</p>
                <p className="text-xs text-amber-700">Customers cannot find you without a location. Tap to set now.</p>
              </div>
            </div>
          </div>
        )}
        
        <Tabs defaultValue="active" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="active" data-testid="active-tab">Active ({activeBookings.length})</TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab">Completed ({completedBookings.length})</TabsTrigger>
          </TabsList>
          
          <TabsContent value="active">
            {isLoading ? (
              <div className="space-y-4">{[1,2].map(i => <div key={`job-skeleton-${i}`} className="animate-pulse bg-stone-200 rounded-2xl h-40" />)}</div>
            ) : activeBookings.length > 0 ? (
              <div className="space-y-4">
                {paginatedActiveBookings.map(booking => <JobCard key={booking.id} booking={booking} showActions />)}
                
                {/* Pagination */}
                {totalActivePages > 1 && (
                  <div className="flex items-center justify-between pt-4 border-t border-stone-200">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setActivePage(p => Math.max(1, p - 1))}
                      disabled={activePage === 1}
                      className="h-9"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {activePage} of {totalActivePages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setActivePage(p => Math.min(totalActivePages, p + 1))}
                      disabled={activePage === totalActivePages}
                      className="h-9"
                    >
                      Next <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Briefcase className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <p className="text-slate-500 mb-2">No active jobs</p>
                <p className="text-sm text-slate-400">Accept bookings to see them here</p>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="history">
            {isLoading ? (
              <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="animate-pulse bg-stone-200 rounded-2xl h-32" />)}</div>
            ) : completedBookings.length > 0 ? (
              <div className="space-y-4">
                {paginatedCompletedBookings.map(booking => <JobCard key={booking.id} booking={booking} />)}
                
                {/* Pagination */}
                {totalHistoryPages > 1 && (
                  <div className="flex items-center justify-between pt-4 border-t border-stone-200">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                      disabled={historyPage === 1}
                      className="h-9"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {historyPage} of {totalHistoryPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setHistoryPage(p => Math.min(totalHistoryPages, p + 1))}
                      disabled={historyPage === totalHistoryPages}
                      className="h-9"
                    >
                      Next <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <p className="text-slate-500">No completed jobs yet</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <ProviderBottomNav />

      {/* Staff Assignment Dialog */}
      <Dialog open={!!showAssignDialog} onOpenChange={(open) => !open && setShowAssignDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Assign Staff to Job
            </DialogTitle>
            <DialogDescription>
              Select a team member to handle this booking
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {staff.length > 0 ? (
              <>
                <div>
                  <Label>Select Staff Member</Label>
                  <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
                    <SelectTrigger className="h-12 mt-1.5" data-testid="staff-select">
                      <SelectValue placeholder="Choose staff" />
                    </SelectTrigger>
                    <SelectContent>
                      {staff.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} ({s.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Assignment Notes (Optional)</Label>
                  <Textarea
                    value={assignmentNotes}
                    onChange={(e) => setAssignmentNotes(e.target.value)}
                    placeholder="Any special instructions for the staff member..."
                    className="mt-1.5"
                    rows={3}
                    data-testid="assignment-notes"
                  />
                </div>
                
                <Button
                  onClick={handleAssignStaff}
                  disabled={isAssigning || !selectedStaffId}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700"
                  data-testid="confirm-assign-btn"
                >
                  {isAssigning ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <UserCheck className="w-5 h-5 mr-2" />
                  )}
                  Assign Staff
                </Button>
              </>
            ) : (
              <div className="text-center py-6">
                <Users className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500 mb-2">No staff members yet</p>
                <p className="text-sm text-slate-400 mb-4">Add team members to assign them to jobs</p>
                <Button
                  onClick={() => { setShowAssignDialog(null); navigate('/provider/staff'); }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Manage Staff
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Customer Dialog */}
      <Dialog open={!!showReportDialog} onOpenChange={(open) => !open && setShowReportDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Report Customer
            </DialogTitle>
            <DialogDescription>
              Report this customer for policy violations. False reports may result in action against your account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Reason for Report *</Label>
              <Select value={reportReason} onValueChange={setReportReason}>
                <SelectTrigger className="h-12 mt-1.5" data-testid="report-reason-select">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_REASONS.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Description *</Label>
              <Textarea
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Describe what happened in detail. Include any relevant information that can help in the investigation..."
                className="mt-1.5"
                rows={4}
                data-testid="report-description"
              />
            </div>
            
            <div className="p-3 bg-amber-50 rounded-xl">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  Reports are reviewed by admin. Users with 3+ valid reports may be suspended. False reports will result in warnings against your account.
                </p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowReportDialog(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReportCustomer}
                disabled={isReporting || !reportReason || !reportDescription.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700"
                data-testid="submit-report-btn"
              >
                {isReporting ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Flag className="w-5 h-5 mr-2" />
                )}
                Submit Report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
